import { useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { getCaseStudyBySlug } from '../lib/casestudies';

export function CaseStudyDetail() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <Navigate to="/casestudies" replace />;
  }

  const caseStudy = getCaseStudyBySlug(slug);

  if (!caseStudy) {
    return <Navigate to="/casestudies" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-4">
            {caseStudy.title}
          </h1>
          <p className="text-xl text-text-gray mb-12">
            {caseStudy.subtitle}
          </p>

          {/* Video Embed */}
          {caseStudy.youtube_id && (
            <div className="my-12 aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${caseStudy.youtube_id}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg shadow-xl"
              ></iframe>
            </div>
          )}

          {/* Markdown Content */}
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => (
                  <h2 className="text-3xl font-bold text-text-dark mb-6 mt-12">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-2xl font-semibold text-text-dark mb-4 mt-8">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-lg text-text-gray leading-relaxed mb-6">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="space-y-4 my-8">
                    {children}
                  </ul>
                ),
                li: ({ children }) => (
                  <li className="flex items-start text-lg text-text-gray">
                    <span className="text-primary-blue mr-3 text-2xl">•</span>
                    <span>{children}</span>
                  </li>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="my-12 bg-primary-blue bg-opacity-10 p-8 rounded-lg border-l-4 border-primary-blue">
                    <div className="text-xl italic text-text-dark">
                      {children}
                    </div>
                  </blockquote>
                ),
                strong: ({ children }) => (
                  <strong className="text-primary-blue font-bold text-2xl">
                    {children}
                  </strong>
                ),
              }}
            >
              {caseStudy.content}
            </ReactMarkdown>
          </div>

          {/* CTA */}
          <div className="my-12 text-center">
            <h2 className="text-3xl font-bold text-text-dark mb-6">
              Ready to Build Your Own Success Story?
            </h2>
            <a
              href="/#book"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-blue hover:bg-dark-blue transition-colors"
            >
              Get Started Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
